/**
 * MessagingPage - Wrapper for the messaging feature
 * Uses existing ConversationView with MessagingProvider
 *
 * Access gated: Requires active subscription
 * Supports gallery image reply via URL parameter: ?replyToGalleryImage=<imageId>
 */

import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MessagingProvider, ConversationView, useMessaging } from '@/features/messaging'
import { useAccessLevel } from '@/hooks/useAccessLevel'
import { BlockedOverlay } from '@/components/ui/BlockedOverlay'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

/**
 * Inner component that handles gallery image reply URL parameter
 * Must be inside MessagingProvider to access context
 */
function MessagingContent() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { setReplyingToGalleryImage } = useMessaging()

  useEffect(() => {
    const galleryImageId = searchParams.get('replyToGalleryImage')
    if (galleryImageId) {
      // Fetch gallery image preview
      const fetchGalleryImage = async () => {
        let success = false
        try {
          const token = localStorage.getItem('auth_token')
          const response = await fetch(`${API_URL}/api/gallery/images/${galleryImageId}/preview`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          })

          if (response.ok) {
            const result = await response.json()
            if (result.data) {
              setReplyingToGalleryImage(result.data)
              console.log('[MessagingPage] Gallery image reply context set:', galleryImageId)
              success = true
            } else {
              console.warn('[MessagingPage] Gallery image preview response missing data')
            }
          } else {
            console.warn('[MessagingPage] Failed to fetch gallery image preview, status:', response.status)
          }
        } catch (error) {
          console.error('[MessagingPage] Error fetching gallery image:', error)
        }

        // Only clear URL parameter if fetch was successful
        // This allows retry on page refresh if fetch failed
        if (success) {
          searchParams.delete('replyToGalleryImage')
          setSearchParams(searchParams, { replace: true })
        }
      }

      fetchGalleryImage()
    }
  }, [searchParams, setSearchParams, setReplyingToGalleryImage])

  return <ConversationView />
}

export function MessagingPage() {
  const { hasAccess, loading } = useAccessLevel()

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b7850]" />
      </div>
    )
  }

  // Block access if no subscription
  if (!hasAccess) {
    return <BlockedOverlay message="Subscription required to access messages" />
  }

  return (
    <MessagingProvider>
      <MessagingContent />
    </MessagingProvider>
  )
}
